// @flow
import React from 'react'

import { getValueForPath } from '../../FilterFunctions'

// components
import { SupplementaryInsuranceRow } from '../../../Components/index'

// types
import type { FieldFormProps } from '../FieldForm'
import type { ResourcesType } from '../../Types/Resources'
import type { FieldDTO } from '../../Types/Field'
import type { CoverageCombinationObject } from '../../Types/TariffsContentType'

type SupplementaryInsuranceCombinationFormProps = FieldFormProps & {
  resources: ResourcesType & {
    coverages: { [name: string]: any },
  },
}

type SupplementaryInsuranceCombinationFormState = {
  parentName: string,
  combinations: Array<CoverageCombinationObject>,
}

export default class SupplementaryInsuranceCombinationForm extends React.Component<
  SupplementaryInsuranceCombinationFormProps,
  SupplementaryInsuranceCombinationFormState,
> {
  static getValuesForField = (props: SupplementaryInsuranceCombinationFormProps, fieldSpecification: FieldDTO<any>) => {
    if (fieldSpecification.domain._type === 'bool') {
      return [true, false]
    } else if (fieldSpecification.domain._type.endsWith('-enum')) {
      return fieldSpecification.domain.values
    } else if (fieldSpecification.domain._type.endsWith('-enum-ref')) {
      const resourcesValue = getValueForPath(fieldSpecification.domain.resourceName, 0, props.resources)
      if (resourcesValue) {
        return resourcesValue
      }
    }
    return []
  }

  static getDerivedStateFromProps(nextProps: SupplementaryInsuranceCombinationFormProps) {
    const { miniAppData } = nextProps
    let changed = false
    const newState = {}
    if (nextProps.resources) {
      const type =
        miniAppData && miniAppData.identifyObject && typeof miniAppData.identifyObject === 'object'
          ? miniAppData.identifyObject.type
          : 'car'
      let parentName = nextProps.prefix.split('-')[0]
      // $FlowIssue
      const resourceContent = getValueForPath(`coverages.${parentName}`, 0, nextProps.resources)
      if (resourceContent.combinationParents) {
        const validValues = resourceContent.combinationParents.reduce((acc, parentFieldName) => {
          const fieldSpecification = nextProps.specification.fields.filter((field) => field.name === parentFieldName)
          if (fieldSpecification.length > 0) {
            // $FlowIssue
            acc[parentFieldName] = SupplementaryInsuranceCombinationForm.getValuesForField(
              nextProps,
              fieldSpecification[0],
            )
          }
          return acc
        }, {})
        newState.combinations = resourceContent.combinations
          .filter(
            (combination) =>
              !combination.whitelist || (Array.isArray(combination.whitelist) && combination.whitelist.includes(type)),
          )
          .reduce((acc, combinationObject: CoverageCombinationObject) => {
            const valid = combinationObject.variant.reduce((validAcc, variantValue, index) => {
              if (validValues[resourceContent.combinationParents[index]]) {
                return validAcc && validValues[resourceContent.combinationParents[index]].includes(variantValue)
              }
              return false
            }, true)
            if (valid) {
              acc.push({
                ...combinationObject,
                value: combinationObject.variant.reduce((valueAcc, variantValue, index) => {
                  valueAcc[resourceContent.combinationParents[index]] = variantValue
                  return valueAcc
                }, {}),
              })
            }
            return acc
          }, [])
      }
      changed = true
      newState.parentName = parentName
    }
    return changed ? newState : null
  }

  state = {
    parentName: '',
    combinations: [],
  }

  handleOnChangeFormData = (key: string, value: Object, combination: Object) => {
    const { formData, onChangeFormData } = this.props
    let data = {}
    if (formData.set) {
      data = formData
        .set(key, value)
        .setIn([key, 'label'], combination.label)
        .setIn([key, 'shortLabel'], combination.shortLabel)
    } else {
      data = {
        [key]: {
          ...value,
          label: combination.label,
          shortLabel: combination.shortLabel,
        },
      }
    }
    onChangeFormData(data, [key, 'label'])
  }

  isTheSame = (value1: Object, value2: Object) =>
    value1 &&
    value2 &&
    Object.keys(value1).length === Object.keys(value2).length &&
    JSON.stringify(value1) === JSON.stringify(value2)

  render() {
    const { content, prefix, id, resources, data, textMode } = this.props
    const { combinations, parentName } = this.state

    if (!content || !content.fieldOrder) {
      return null
    }

    if (textMode) {
      const selected = combinations.filter((combination) =>
        this.isTheSame(data, { ...combination.value, label: combination.label, shortLabel: combination.shortLabel }),
      )
      if (selected.length) {
        return selected[0].label
      }
      return null
    }

    const resourceContent = resources.coverages[parentName]
    const priceText =
      resourceContent && resourceContent.priceTranslationKey
        ? resourceContent.priceTranslationKey
        : 'general.pricePerYear'
    return (
      <div className="p-2 mw-100">
        {combinations.map((combination) => [
          <SupplementaryInsuranceRow
            id={`${prefix}${id}`}
            key={`${prefix}${id}-row`}
            label={combination.label}
            onSelect={(value: Object | string) => {
              this.handleOnChangeFormData(parentName, value, combination)
            }}
            priceText={combination.price}
            priceTranslationKey={priceText}
            selected={this.isTheSame(data, {
              ...combination.value,
              label: combination.label,
              shortLabel: combination.shortLabel,
            })}
            value={combination.value}
          />,
          <hr className="border-0 border-sm-top" key={`${prefix}${id}-hr`} />,
        ])}
      </div>
    )
  }
}
